import { DateTime } from 'luxon'
import { PGA_COM_HOST } from 'env'
import {
  formatTurnaround,
  OFFERING_KIND,
} from 'pages/pga-coach/bookings/booking-types/offering-kind'
import { proficiencyOptions } from 'pages/club/golfer-intake-form/constants'
import {
  isHttpsUrl,
  REQUEST_STATUS,
} from 'pages/pga-coach/virtual-coaching/utils'

export { isHttpsUrl, REQUEST_STATUS }

export const MAX_VIDEOS = 3

// Own copy (not the coach side's helper) so the golfer pages share no code
// with the coach pages beyond the status constants and `isHttpsUrl`.
export const formatDateTime = isoString =>
  isoString
    ? DateTime.fromISO(isoString).toFormat("LLL d, yyyy 'at' h:mm a")
    : '—'

export const REQUESTS_PATH = '/club/virtual-coaching'

export const getRequestPath = requestId =>
  `${REQUESTS_PATH}/${encodeURIComponent(requestId)}`

export const getUploadPath = requestId => `${getRequestPath(requestId)}/upload`

// The account payments transaction page for a request's purchase, or null while
// the backend does not return a transaction id.
export const getTransactionPath = transaction =>
  transaction?.id ? `/account/payments/transactions/${transaction.id}` : null

export const getCheckoutPath = (idOrSlug, lessonTypeId) =>
  `/coach/${idOrSlug}/purchase/virtual-coaching/${lessonTypeId}`

export const getCoachProfileUrl = idOrSlug =>
  `${PGA_COM_HOST}/coach/${idOrSlug}`

export const getMessageCoachPath = coach =>
  coach?.externalId
    ? `/club/messages/${coach.externalId}/new-conversation`
    : null

export const getCoachIdOrSlug = coach =>
  coach?.coachProfile?.slug || coach?.externalId

export const getCoachFirstName = coach =>
  coach?.name?.trim().split(/\s+/)[0] || 'Your coach'

// "within 48 hours", "within 1 week"; null when the offering has no turnaround.
export const formatTurnaroundWithin = hours => {
  const label = formatTurnaround(hours)
  return label ? `within ${label}` : null
}

// An offering is purchasable until `expiresAt`. The backend returns null for
// offerings golfers cannot buy; this also guards a clock that has just passed
// `expiresAt` and a lesson type that is not a virtual offering.
export const isOfferingAvailable = (offering, now = DateTime.local()) => {
  if (!offering) return false
  if (
    offering.offeringKind &&
    offering.offeringKind !== OFFERING_KIND.VIRTUAL_COACHING
  ) {
    return false
  }
  const expiresAt = offering.virtualCoaching?.expiresAt
  if (!expiresAt) return true
  const expires = DateTime.fromISO(expiresAt)
  return !expires.isValid || expires > now
}

export const formatDueDate = dueAt => {
  const due = dueAt ? DateTime.fromISO(dueAt) : null
  return due?.isValid ? due.toFormat('ccc, LLL d') : null
}

export const formatFileSize = bytes => {
  if (bytes == null) return ''
  if (bytes < 1024 * 1024) return `${Math.max(Math.round(bytes / 1024), 1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Cloudinary can render a still from any video by changing the extension and
// asking for the first frame. Other hosts get no thumbnail.
export const getVideoThumbnailUrl = url => {
  try {
    const parsed = new URL(url)
    if (
      parsed.hostname !== 'res.cloudinary.com' ||
      !parsed.pathname.includes('/video/upload/')
    ) {
      return null
    }
    parsed.pathname = parsed.pathname
      .replace('/video/upload/', '/video/upload/so_0,w_640,c_limit/')
      .replace(/\.[a-z0-9]+$/i, '.jpg')
    return parsed.toString()
  } catch (error) {
    return null
  }
}

export const STATUS_STEP = {
  [REQUEST_STATUS.PURCHASED]: 1,
  [REQUEST_STATUS.READY_TO_ANALYZE]: 2,
  [REQUEST_STATUS.COMPLETE]: 3,
}

export const STUDENT_STATUS_LABELS = {
  [REQUEST_STATUS.PURCHASED]: 'Upload your video',
  [REQUEST_STATUS.READY_TO_ANALYZE]: 'Coach reviewing',
  [REQUEST_STATUS.COMPLETE]: 'Feedback ready',
}

// Same levels and wording as the golfer intake form. The coach sees the label
// (for example "Familiar"), so that is what gets submitted.
export const GOLF_LEVEL_OPTIONS = proficiencyOptions.map(
  ({ label, supportText }) => ({ value: label, label, supportText }),
)
